// Stable e2e hooks for antd Select components.
//
// rc-select forwards `data-*`/`aria-*` attributes (via rc-util `pickAttrs`) onto
// both the `.ant-select` root and each rendered `.ant-select-item-option` node, so
// these helpers let Playwright target a select trigger, scope its portal-rendered
// dropdown, and click an option by value instead of by fragile visible text.

export type SelectTestProps = {
  'data-cy': string
  popupClassName: string
}

export type OptionTestProps = {
  'data-cy': string
}

/**
 * Spread onto an antd `<Select>` to give e2e a stable trigger hook plus a uniquely
 * scopable dropdown. The popup class (`<cy>-popup`) lets tests scope option lookups
 * to exactly this select, avoiding cross-talk between simultaneously open dropdowns.
 */
export const selectTestProps = (cy: string): SelectTestProps => ({
  'data-cy': cy,
  popupClassName: `${cy}-popup`,
})

/**
 * Spread into an antd option object (or onto an `<Option>`) so e2e can click it by
 * value. `key` must be a stable id/value, never the visible label.
 */
export const optionTestProps = (key: string | number): OptionTestProps => ({
  'data-cy': `option--${key}`,
})

/**
 * Map an array of antd options to carry a per-option `data-cy=option--<value>` hook,
 * keyed on each option's `value`. Use for selects built from a dynamic options array.
 */
export const withOptionTestIds = <T extends { value: string | number }>(
  options: T[],
): (T & OptionTestProps)[] =>
  options.map((opt) => ({ ...opt, ...optionTestProps(opt.value) }))

/**
 * Like {@link withOptionTestIds} but tolerant of antd's grouped option shape: an
 * entry with an `options` array is treated as an OptGroup and its children are
 * tagged; a leaf entry (with a `value`) is tagged directly. Used by selects whose
 * option list may be flat or grouped (e.g. the document-usage picker).
 */
export const withGroupedOptionTestIds = <
  T extends { value?: string | number; options?: { value: string | number }[] },
>(
  options: T[],
): T[] =>
  options.map((opt) =>
    Array.isArray(opt.options)
      ? { ...opt, options: withOptionTestIds(opt.options) }
      : opt.value !== undefined
        ? { ...opt, ...optionTestProps(opt.value) }
        : opt,
  )
